import React, { useEffect, useState } from 'react'
import { EditionModeContextProvider, EditionModes, useEditionMode } from './providers/editionModeContext'
import { useField, useFormikContext } from 'formik'
import { isEqual } from 'lodash-es'

export interface HoverProp {
  disableHoverEdition?: boolean
}

interface HoverEditionHandlerInterface {
  handleFocus: () => void
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  setDisplayOnLeave: (() => void) | undefined
  setEditionOnEnter: (() => void) | undefined
  handleOpen: () => void
  handleClose: () => void
}

export const withHoverEditionMode =
  <P extends HoverProp>(Component: React.FC<P>): React.FC<P> =>
  (props: HoverProp) => {
    return (
      <>
        {!props.disableHoverEdition ? (
          <EditionModeContextProvider>
            <Component {...(props as P)} />
          </EditionModeContextProvider>
        ) : (
          <Component {...(props as P)} />
        )}
      </>
    )
  }

export const useHoverEditionHandlers = (name: string, disableHoverEdition: boolean): HoverEditionHandlerInterface => {
  const [isFocused, setIsFocused] = useState(false)
  const [, setEditionMode] = useEditionMode()
  const [, meta] = useField({ name })
  const formik = useFormikContext()

  const isDifferent = !isEqual(meta.value, meta.initialValue)

  const setDisplay = (): void => {
    if (disableHoverEdition || isFocused || isDifferent) return

    setEditionMode(EditionModes.Display)
  }

  useEffect(() => {
    if (disableHoverEdition) return
    formik.isSubmitting && setDisplay()
  }, [formik.isSubmitting])

  useEffect(() => {
    if (disableHoverEdition) return
    setDisplay()
  }, [meta.value, meta.initialValue])

  const setEdition = (): void => setEditionMode(EditionModes.Edition)

  const handleFocus = (): void => {
    setIsFocused(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    if (e.relatedTarget !== e.currentTarget) {
      setIsFocused(false)
      setDisplay()
    }
    formik.handleBlur(e)
  }

  const handleOpen = (): void => {
    setIsFocused(true)
  }

  const handleClose = (): void => {
    setIsFocused(false)
    setDisplay()
  }

  return {
    handleFocus,
    handleBlur,
    handleOpen,
    handleClose,
    setDisplayOnLeave: disableHoverEdition ? undefined : setDisplay,
    setEditionOnEnter: disableHoverEdition ? undefined : setEdition,
  }
}
